import { getManifestI18n, isCurrencySupportedByManifest } from "./manifestI18n.js";
import { REGISTRY_CURRENCIES, normalizeRegistryCurrency } from "./currencyRegistry.js";
import {
  readClientViewCurrencyCookie,
  writeClientViewCurrencyCookie,
} from "./clientViewPreferences.js";

export const SUPPORTED_CURRENCIES = REGISTRY_CURRENCIES;
export const DEFAULT_CURRENCY = "COP";

export const CURRENCY_STORAGE_KEY = "midpoint_display_currency";

export function normalizeDisplayCurrency(value) {
  return normalizeRegistryCurrency(value);
}

function readStoredCurrency() {
  try {
    return localStorage.getItem(CURRENCY_STORAGE_KEY);
  } catch {
    return null;
  }
}

function constrainClientViewCurrency(currency, manifest) {
  const normalized = normalizeDisplayCurrency(currency);
  if (!manifest) {
    return normalized;
  }
  const manifestI18n = getManifestI18n(manifest);
  if (manifestI18n.supportedCurrencies.includes(normalized)) {
    return normalized;
  }
  return manifestI18n.defaultCurrency;
}

/**
 * @param {object} options
 * @param {string|null} [options.storedCurrency]
 * @param {object|null} [options.accountPreferences]
 * @param {object|null} [options.manifest]
 */
export function resolveDisplayCurrency({
  storedCurrency = null,
  accountPreferences = null,
  manifest = null,
  surface = null,
} = {}) {
  const manifestDefault = manifest ? getManifestI18n(manifest).defaultCurrency : null;

  if (surface === "clientView" && manifest) {
    const persistedCurrency =
      readPersistedClientViewCurrency() || storedCurrency || readStoredCurrency();
    if (persistedCurrency) {
      return constrainClientViewCurrency(normalizeDisplayCurrency(persistedCurrency), manifest);
    }
    return constrainClientViewCurrency(manifestDefault || DEFAULT_CURRENCY, manifest);
  }

  const candidates = [
    accountPreferences?.displayCurrency,
    storedCurrency ?? readStoredCurrency(),
    manifestDefault,
    DEFAULT_CURRENCY,
  ];
  for (const candidate of candidates) {
    if (candidate) {
      const resolved = normalizeDisplayCurrency(candidate);
      return manifest ? constrainClientViewCurrency(resolved, manifest) : resolved;
    }
  }
  return manifest ? constrainClientViewCurrency(DEFAULT_CURRENCY, manifest) : DEFAULT_CURRENCY;
}

export function readDisplayCurrencyFromStorage() {
  return readStoredCurrency();
}

export function readPersistedClientViewCurrency() {
  return readClientViewCurrencyCookie() || readStoredCurrency();
}

export function writeDisplayCurrencyToStorage(currency) {
  const normalized = normalizeDisplayCurrency(currency);
  writeClientViewCurrencyCookie(normalized);
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, normalized);
  } catch {
    // ignore storage failures; cookie still persists the preference
  }
}

export function getSupportedCurrencies(manifest) {
  return getManifestI18n(manifest).supportedCurrencies;
}

export function isCurrencyAllowed(currency, manifest) {
  return isCurrencySupportedByManifest(currency, getManifestI18n(manifest));
}
